import React, { useMemo, useRef, useState } from "react";
import { Autocomplete, GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { toast } from "react-hot-toast";
import { useUserContext } from "../context/UserContext";

const initialForm = {
  countryCode: "+91",
  mobileNumber: "",
  email: "",
  firstName: "",
  lastName: "",
  address: "",
  pincode: "",
};

export default function AddCustomerPage() {
  const { token } = useUserContext();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 });
  const [markerPosition, setMarkerPosition] = useState(null);
  const autocompleteRef = useRef(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const libraries = useMemo(() => ["places"], []);
  const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries,
  });

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function extractPincode(addressComponents = []) {
    const postalCodeComp = addressComponents.find((comp) => comp.types?.includes("postal_code"));
    return postalCodeComp?.long_name || "";
  }

  function updateAddressFromPlace(place) {
    if (!place) return;
    const formattedAddress = place.formatted_address || place.name || "";
    const postalCode = extractPincode(place.address_components || []);

    setForm((prev) => ({
      ...prev,
      address: formattedAddress || prev.address,
      pincode: postalCode || prev.pincode,
    }));

    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const loc = { lat, lng };
      setMapCenter(loc);
      setMarkerPosition(loc);
    }
  }

  function onAutocompleteLoad(autocomplete) {
    autocompleteRef.current = autocomplete;
  }

  function onPlaceChanged() {
    const place = autocompleteRef.current?.getPlace();
    updateAddressFromPlace(place);
  }

  function reverseGeocode(latLng) {
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const best = results[0];
        const postalCode = extractPincode(best.address_components || []);
        setForm((prev) => ({
          ...prev,
          address: best.formatted_address || prev.address,
          pincode: postalCode || prev.pincode,
        }));
      }
    });
  }

  function onMapClick(event) {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();
    if (typeof lat !== "number" || typeof lng !== "number") return;
    const loc = { lat, lng };
    setMarkerPosition(loc);
    setMapCenter(loc);
    reverseGeocode(loc);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!token) {
      toast.error("Login required to add customer");
      return;
    }

    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${apiBase}/admin/register-customer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "Failed to register customer");
      }

      toast.success(data.message || "Customer added successfully");
      setForm(initialForm);
      setMapCenter({ lat: 20.5937, lng: 78.9629 });
      setMarkerPosition(null);
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full min-h-screen px-6 py-4">
      <div className="mx-auto max-w-6xl rounded-xl bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-bold text-gray-900">Add Customer</h1>
        <p className="mb-6 text-sm text-gray-500">Create a customer account with map-assisted address selection.</p>

        {loadError && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            Google Maps failed to load. Set REACT_APP_GOOGLE_MAPS_API_KEY to enable location search.
          </div>
        )}

        {isMapLoaded && (
          <div className="mb-6 space-y-3">
            <label className="block text-sm font-medium text-gray-700">Search Location</label>
            <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
              <input
                type="text"
                placeholder="Search place, area or landmark"
                className="w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
              />
            </Autocomplete>
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <GoogleMap
                center={markerPosition || mapCenter}
                zoom={markerPosition ? 15 : 5}
                mapContainerStyle={{ width: "100%", height: "300px" }}
                onClick={onMapClick}
                options={{ streetViewControl: false, mapTypeControl: false }}
              >
                {markerPosition && <MarkerF position={markerPosition} />}
              </GoogleMap>
            </div>
            <p className="text-xs text-gray-500">Tip: Search location or click on map to auto-fill address and pincode.</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">
            Country Code
            <input
              name="countryCode"
              value={form.countryCode}
              onChange={onChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
            />
          </label>

          <label className="text-sm font-medium text-gray-700">
            Mobile Number
            <input
              name="mobileNumber"
              value={form.mobileNumber}
              onChange={onChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
            />
          </label>

          <label className="text-sm font-medium text-gray-700">
            Email
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
            />
          </label>

          <label className="text-sm font-medium text-gray-700">
            First Name
            <input
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
            />
          </label>

          <label className="text-sm font-medium text-gray-700">
            Last Name
            <input
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
            />
          </label>

          <label className="text-sm font-medium text-gray-700">
            Pincode
            <input
              name="pincode"
              value={form.pincode}
              onChange={onChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
            />
          </label>

          <label className="text-sm font-medium text-gray-700 md:col-span-2">
            Address
            <input
              name="address"
              value={form.address}
              onChange={onChange}
              required
              className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 outline-none focus:border-indigo-500"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-2 rounded-lg bg-indigo-600 px-4 py-2.5 font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
          >
            {submitting ? "Adding Customer..." : "Add Customer"}
          </button>
        </form>
      </div>
    </div>
  );
}
